/**
 * Globale Header-Navigation für alle authentifizierten Routen.
 *
 * Top-Level-Sektionen (Dashboard / Workshops / Brand-Briefs / Admin) sind hier
 * verlinkt — kontext-spezifische Sub-Navigation lebt weiterhin in den jeweiligen
 * Sub-Layouts (z.B. AdminLayout-Sidebar).
 */
import { useTranslation } from "react-i18next";
import { Link, NavLink, useNavigate } from "react-router-dom";

import { useLogout } from "@/auth/hooks";
import { useAuthStore } from "@/auth/store";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { i18n, SUPPORTED_LOCALES } from "@/lib/i18n";
import { cn } from "@/lib/utils";

interface NavItem {
  to: string;
  labelKey: string;
  adminOnly?: boolean;
}

const NAV: NavItem[] = [
  { to: "/dashboard", labelKey: "nav.dashboard" },
  { to: "/customers", labelKey: "nav.customers", adminOnly: true },
  { to: "/workshops", labelKey: "nav.workshops" },
  { to: "/brand-briefs", labelKey: "nav.brand_briefs" },
  { to: "/admin", labelKey: "nav.admin", adminOnly: true },
];

export function AppHeader() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const logout = useLogout();
  const navigate = useNavigate();
  const isAdminish = user?.role === "admin" || user?.role === "owner";

  function handleLogout(): void {
    logout.mutate(undefined, {
      onSettled: () => navigate("/login", { replace: true }),
    });
  }

  function handleLocaleChange(event: React.ChangeEvent<HTMLSelectElement>): void {
    void i18n.changeLanguage(event.target.value);
  }

  const items = NAV.filter((n) => !n.adminOnly || isAdminish);

  return (
    <header className="sticky top-0 z-30 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/75">
      <div className="container flex h-14 items-center gap-6">
        <Link
          to="/dashboard"
          className="whitespace-nowrap transition-opacity hover:opacity-80"
        >
          <Logo size={1.05} />
        </Link>

        <nav className="flex flex-1 items-center gap-1 text-sm overflow-x-auto">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/dashboard"}
              className={({ isActive }) =>
                cn(
                  "rounded-md px-3 py-1.5 transition-colors whitespace-nowrap",
                  isActive
                    ? "bg-accent text-accent-foreground"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground",
                )
              }
            >
              {t(item.labelKey)}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 text-sm">
          <span className="hidden text-muted-foreground sm:inline">
            {user?.full_name}
          </span>
          <ThemeToggle />
          <select
            value={i18n.language.split("-")[0]}
            onChange={handleLocaleChange}
            aria-label={t("nav.language")}
            className="h-8 rounded-md border border-input bg-background px-2 text-xs"
          >
            {SUPPORTED_LOCALES.map((code) => (
              <option key={code} value={code}>
                {code.toUpperCase()}
              </option>
            ))}
          </select>
          <Button
            variant="outline"
            size="sm"
            onClick={handleLogout}
            disabled={logout.isPending}
          >
            {t("common.logout")}
          </Button>
        </div>
      </div>
    </header>
  );
}
